import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { LockBankAccountDialogComponent } from 'src/app/helper/edit-case-dialog/lock-bank-account-dialog/lock-bank-account-dialog.component';
import { UnlockBankAccountDialogComponent } from 'src/app/helper/edit-case-dialog/unlock-bank-account-dialog/unlock-bank-account-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PartyType } from 'src/types';
import {
  ClientDataTableItem,
  ClientsService,
} from 'src/app/services/clients/clients.service';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { partyType } from 'src/app/components/dashboard/enums/enum-descriptors';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchDetailedItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { AxiosError } from 'axios';
import {
  CountriesService,
  Country,
} from 'src/app/services/countries/countries.service';
import { extractIBAN } from 'ibantools';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeIconButtonComponent } from '../../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-edit-client-dialog',
  templateUrl: './edit-client-dialog.component.html',
  styleUrls: ['./edit-client-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    MatDialogClose,
  ],
})
export class EditClientDialogComponent implements OnInit, OnDestroy {
  private client = inject<ClientDataTableItem>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private clientsService = inject(ClientsService);
  private enumsService = inject(EnumsService);
  private helpersService = inject(HelpersService);
  private countriesService = inject(CountriesService);
  private companySearchService = inject(CompanySearchService);
  private dialog = inject(MatDialog);
  private countries: Country[] = [];
  private partyTypes: PartyType[] = [];
  private readonly destroy = new Subject<void>();

  dialogRef = inject<MatDialogRef<EditClientDialogComponent>>(MatDialogRef);

  readonly formGroup = this.fb.group({
    tax_number: this.fb.control<string | null>(this.client.tax_number),
    name: this.fb.control(this.client.name, {
      validators: Validators.required,
      nonNullable: true,
    }),
    party_type_id: this.fb.control<number | null>(this.client.party_type_id, {
      validators: Validators.required,
    }),
    address: this.fb.group({
      country: this.fb.control(this.client.address.country ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
      settlement: this.fb.control(this.client.address.settlement ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
      postcode: this.fb.control(this.client.address.postcode ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
      street: this.fb.control(this.client.address.street ?? '', {
        validators: Validators.required,
        nonNullable: true,
      }),
    }),
    bankAccount: this.fb.group({
      account_number: this.fb.control<string | null>(
        this.client.bankAccount.account_number,
        { validators: this.helpersService.bankAccountValidator },
      ),
      iban: this.fb.control<string | null>(this.client.bankAccount.iban, {
        validators: this.helpersService.bankAccountValidator,
      }),
      bic: this.fb.control<string | null>(this.client.bankAccount.bic),
      bank_name: this.fb.control<string | null>(
        this.client.bankAccount.bank_name,
      ),
      account_holder_name: this.fb.control(
        this.client.bankAccount.account_holder_name ?? '',
        { validators: Validators.required, nonNullable: true },
      ),
      lock_bank_account: this.fb.control(
        this.client.bankAccount.lock_bank_account ?? false,
        { nonNullable: true },
      ),
    }),
  });

  submitLoading = false;
  readonly initLoading = signal(false);

  countryOptions: string[] = [];

  partyTypeOptions: number[] = [];

  type?: 'ind' | 'org';

  get address() {
    return this.formGroup.controls.address;
  }
  get country() {
    return this.address.controls.country;
  }
  get settlement() {
    return this.address.controls.settlement;
  }
  get postcode() {
    return this.address.controls.postcode;
  }
  get street() {
    return this.address.controls.street;
  }
  get bankAccount() {
    return this.formGroup.controls.bankAccount;
  }
  get account_number() {
    return this.bankAccount.controls.account_number;
  }
  get iban() {
    return this.bankAccount.controls.iban;
  }
  get bic() {
    return this.bankAccount.controls.bic;
  }
  get bank_name() {
    return this.bankAccount.controls.bank_name;
  }
  get account_holder_name() {
    return this.bankAccount.controls.account_holder_name;
  }
  get lock_bank_account() {
    return this.bankAccount.controls.lock_bank_account;
  }
  get tax_number() {
    return this.formGroup.controls.tax_number;
  }
  get name() {
    return this.formGroup.controls.name;
  }
  get party_type_id() {
    return this.formGroup.controls.party_type_id;
  }

  constructor() {
    if (!this.client.bankAccount.lock_bank_account) {
      this.bankAccount.disable();
    }
  }

  async ngOnInit(): Promise<void> {
    this.iban.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: (value) => {
        try {
          const ibanResult = extractIBAN(value ?? '');
          if (ibanResult.valid) {
            this.account_number.patchValue(ibanResult.bban ?? null);
          }
        } catch (error) {
          console.error('Error while extracting iban', error);
        }
      },
    });
    try {
      this.initLoading.set(true);

      const [partyTypes, countries] = await Promise.all([
        this.enumsService.getEnum<PartyType>(partyType.model),
        this.countriesService.getCountries(),
      ]);

      this.partyTypes = partyTypes;
      this.partyTypeOptions = partyTypes.map((pt) => pt.id);

      this.countries = countries.countries;
      this.countryOptions = this.countries.map((c) => c.iso);

      if (!this.isClientHouse() && this.client.tax_number) {
        const detailedSearch = await this.companySearchService.detailedSearch(
          this.client.tax_number.slice(0, 8),
        );

        if (detailedSearch?.company) {
          this.updateByDetailedSearch(detailedSearch.company);
        }
      }
    } catch (error) {
      console.error(error);
      this.snackbar.open('Valami hiba történt a dialog megnyitásakor', 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
    } finally {
      this.initLoading.set(false);
    }
  }

  openLockBankAccountDialog(): void {
    this.dialog.open(LockBankAccountDialogComponent, {
      data: { bank_account: this.bankAccount },
    });
  }

  openUnlockBankAccountDialog(): void {
    this.dialog.open(UnlockBankAccountDialogComponent, {
      data: { bank_account: this.bankAccount },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  onCompanySelected(item: CompanySearchAutocompleteItem) {
    this.tax_number.patchValue(item.tax_number);
    this.party_type_id.patchValue(item.party_type?.id ?? null);
  }

  async submit() {
    if (this.formGroup.invalid || this.submitLoading) {
      return;
    }

    try {
      this.submitLoading = true;

      const value = this.formGroup.getRawValue();

      const result: ClientDataTableItem =
        await this.clientsService.updateClient(this.client.id, {
          name: value.name,
          address: {
            country_id: this.countries.find(
              (c) => value.address.country === c.iso,
            )?.id as number,
            postcode: value.address.postcode,
            settlement: value.address.settlement,
            street: value.address.street,
          },
          bankAccount: {
            account_holder_name: value.bankAccount.account_holder_name,
            account_number: value.bankAccount.account_number || undefined,
            bank_name: value.bankAccount.bank_name || undefined,
            bic: value.bankAccount.bic || undefined,
            iban: value.bankAccount.iban || undefined,
          },
          lock_bank_account: value.bankAccount.lock_bank_account,
          party_type_id: value.party_type_id,
          tax_number: value.tax_number || null,
        });
      Object.assign(this.client, result);

      this.dialogRef.close(value.name);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          this.helpersService.markAllChildrenAsDirty(this.formGroup);
          const errors: Record<string, string[]> = error.response.data.errors;
          Object.entries(errors).forEach(([key, values]) => {
            const formControl = this.formGroup.get(key);
            if (formControl) {
              formControl.setErrors({
                backend: values[0],
              });
            } else {
              this.formGroup.setErrors({
                backend: values[0],
              });
            }
          });
          return;
        }
      }
      console.error('Error while updating client', error);
      this.snackbar.open('Valami hiba történt mentéskor!', 'OK', {
        duration: 5000,
      });
      this.dialogRef.close();
    } finally {
      this.submitLoading = false;
    }
  }

  readonly partyTypeDisplayWith = (id: number): string => {
    return this.partyTypes.find((t) => t.id === id)?.label || '';
  };

  readonly countryDisplayWith = (iso: string): string => {
    return this.countries.find((t) => t.iso === iso)?.name || '';
  };

  private isClientHouse(): boolean {
    const clientPartyType = this.partyTypes.find(
      (pt) => pt.id === this.party_type_id.value,
    );
    return clientPartyType?.internal_id === '91-a';
  }

  private updateByDetailedSearch(company: CompanySearchDetailedItem): void {
    if (!this.lock_bank_account.value) {
      this.bankAccount.patchValue({
        account_number:
          company.bankAccount?.account_number || this.account_number.value,
        iban: company.bankAccount?.iban || this.iban.value,
        bank_name: company.bankAccount?.bank_name || this.bank_name.value,
        account_holder_name:
          company.bankAccount?.account_holder_name ||
          this.account_holder_name.value,
      });
    }

    const country = this.countries.find(
      (v) => v.id === company.address?.country_id,
    );
    this.address.patchValue({
      country: country?.iso ?? this.country.value,
      settlement: company.address?.settlement ?? this.settlement.value,
      postcode: company.address?.postcode ?? this.postcode.value,
      street: company.address?.street ?? this.street.value,
    });

    if (company.name) {
      this.name.patchValue(company.name);
    }
  }
}
